import {
  SubContent as RadixSubContent,
  Portal as RadixPortal,
} from '@radix-ui/react-dropdown-menu';
import {
  forwardRef,
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import * as styles from './Menu.sty.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { WithChildren } from '../../core/types/with-children.js';

/**
 * @public
 */
export type MenuSubContentProps = WithChildren;

const SUB_CONTENT_SIDE_OFFSET = 0; // NOTE: This effectively results in an offset of -4px due to the default behavior of radix-ui sub-menus

/**
 * @internal
 */
export const ALIGN_OFFSET = -4;

/**
 * The component that pops out when the dropdown menu is open.
 * @public
 */
export const SubContent = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuSubContentProps
>((props, forwardedRef) => {
  const { _onTabKeyPressed, children } = props as MenuSubContentProps & {
    _onTabKeyPressed: () => void;
  };
  const tabKeyHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (_onTabKeyPressed && e.key === 'Tab') {
        _onTabKeyPressed();
      }
    },
    [_onTabKeyPressed],
  );
  const { style } = useOverlayStacking(undefined, 'overlay');

  return (
    <RadixPortal>
      <RadixSubContent
        ref={forwardedRef}
        sideOffset={SUB_CONTENT_SIDE_OFFSET}
        alignOffset={ALIGN_OFFSET}
        onKeyUp={tabKeyHandler}
        className={styles.content}
        style={{ zIndex: style.zIndex }}
        collisionPadding={2}
      >
        {children}
      </RadixSubContent>
    </RadixPortal>
  );
});

(SubContent as typeof SubContent & { displayName: string }).displayName =
  'Menu.SubContent';
